import React from 'react'
import { Spin, Tooltip } from '@surecompanies/core_components'
import DuplicateGroups, { parseDuplicateGroups } from './DuplicateGroups'
import Section from './Section'
import StageRail from './StageRail'
import { withCompanyName } from './client-key-label'
import { CensusRunStepDto, CensusRunStepStatus, JsonValue } from './types'

/**
 * The canonical step list for an API integration sync, in execution order, as
 * it runs in partner-census-graph's CensusService. The backend only writes a
 * row for a step that actually ran, so this list supplies the rest as
 * "Not reached".
 */
export const API_STEPS: { seq: number; stepKey: string; name: string }[] = [
  { seq: 1, stepKey: 'CONFIGURATION', name: 'Configuration' },
  { seq: 2, stepKey: 'CLIENT_ENABLED_CHECK', name: 'Client Enabled Check' },
  { seq: 3, stepKey: 'DUPLICATE_CHECK', name: 'Duplicate Check' },
  { seq: 4, stepKey: 'CSV_TO_JSON', name: 'Convert CSV to JSON' },
  { seq: 5, stepKey: 'IDENTIFY_RECORDS', name: 'Identify Records' },
  { seq: 6, stepKey: 'IMPORT_ID', name: 'Import ID' },
  { seq: 7, stepKey: 'AUTO_STOP_COVERAGE', name: 'Auto Stop Coverage' },
  { seq: 8, stepKey: 'SAVE_HRIS_SYNC', name: 'Save to HRIS Sync Table' },
  {
    seq: 9,
    stepKey: 'PUSH_CENSUS_MANAGEMENT',
    name: 'Push to Census Management',
  },
  { seq: 10, stepKey: 'HRIS_NOTIFY', name: 'HRIS Notify' },
  { seq: 11, stepKey: 'UPDATE_IMPORT_ID', name: 'Update HRIS Sync Import ID' },
  { seq: 12, stepKey: 'MOVE_FILE', name: 'Move File' },
  { seq: 13, stepKey: 'UPDATE_STATUS', name: 'Update Census Status' },
]

export const TONE: Record<
  CensusRunStepStatus | 'PENDING',
  { fg: string; dot: string }
> = {
  OK: { fg: '#0f766e', dot: '#0f766e' },
  ERROR: { fg: '#7a1f22', dot: '#dc2626' },
  WARN: { fg: '#7a4d0a', dot: '#d97706' },
  SKIPPED: { fg: '#6b7280', dot: '#ffffff' },
  PENDING: { fg: '#9ca3af', dot: '#ffffff' },
}

export const formatDuration = (ms?: number): string => {
  if (ms === undefined || ms === null) return ''
  if (ms < 1000) return `${ms}ms`
  return `${(ms / 1000).toFixed(1)}s`
}

const formatValue = (value: JsonValue): string => {
  if (value === null || value === undefined) return '—'
  // An empty string is a real answer — e.g. a client with no eligibility rule
  // configured — so show it rather than rendering an empty cell.
  if (value === '') return '(empty)'
  if (typeof value === 'object') return JSON.stringify(value)
  return String(value)
}

/** Rules arrive as an object from the template and as a JSON string from the
 *  legacy sources, so parse before indenting. */
const formatRule = (rule: JsonValue): string => {
  const parsed = typeof rule === 'string' ? tryParse(rule) : rule
  if (parsed === null || parsed === undefined || parsed === '') return '—'
  return JSON.stringify(parsed, null, 2)
}

const tryParse = (value: string): JsonValue => {
  try {
    return JSON.parse(value)
  } catch {
    return value
  }
}

/** The values a key can hold, listed on hover: seeing one value tells a reader
 *  nothing about what the alternatives were. */
const KEY_HINTS: Record<string, string[]> = {
  mappingSource: ['template', 'hardcoded'],
  rulesSource: ['onboarding-template', 'customer-config', 'hardcoded'],
  mappingVersion: ['v1, v2, …', 'hardcoded'],
  activityLogAwaited: ['true', 'false'],
}

/** One value per line: a list on one line reads as a single odd value. */
/** Keys whose meaning, not whose values, is what a reader needs. */
const KEY_NOTES: Record<string, string> = {
  duplicateRows:
    'Rows sharing an SSN, EmailAddress or CompanyEmployeeId with another row in the same file are marked as errors and counted as duplicates.',
  skippedNoEpsEmployee:
    'The SSN matched nobody in Enrollment Platform. There is no coverage to stop, so the person was skipped. This is normal.',
  skippedNotEmployee:
    'The SSN matched a dependent rather than the employee. Terminating on it would end the subscriber’s coverage and the whole family would lose it, so it was refused. The earlier lookup should return employees only, so any count here is worth investigating.',
  processorFilteredOut:
    'Rows a client’s own processor dropped on its way through, which is written in code rather than configured. Only one client does this today — it drops rows in contribution group 19 — so this reads 0 for everyone else. Nothing to do with the ignore rules above; it lands here because it happens at the same point in the run.',
}

/** Both kinds of hint earn the dotted underline that invites a hover. */
const hasHint = (key: string): boolean =>
  Boolean(KEY_HINTS[key] || KEY_NOTES[key])

const describeValues = (values: string[]): React.ReactNode => (
  <div>
    <div style={{ fontWeight: 600, marginBottom: 4 }}>Possible values</div>
    {values.map((value) => (
      <div key={value}>- {value}</div>
    ))}
  </div>
)

/**
 * The order the outcomes of a run read in: what went through, what failed, what
 * was held back.
 *
 * Sorted here because the order the backend wrote them in is lost — the detail
 * is stored as jsonb, which does not keep key order.
 */
const OUTCOME_ORDER = ['valid', 'error', 'ineligible']

const outcomeRank = (key: string): number => {
  const lower = key.toLowerCase()
  const index = OUTCOME_ORDER.findIndex((token) => lower.startsWith(token))
  return index === -1 ? -1 : index
}

/** Stable: keys that name no outcome keep the order they arrived in. */
const sortByOutcome = (entries: [string, JsonValue][]): [string, JsonValue][] =>
  entries
    .map((entry, index) => ({ entry, index, rank: outcomeRank(entry[0]) }))
    .sort((a, b) => {
      if (a.rank === b.rank) return a.index - b.index
      if (a.rank === -1) return -1
      if (b.rank === -1) return 1
      return a.rank - b.rank
    })
    .map(({ entry }) => entry)

/**
 * A version is a number that identifies something, not one that counts it. It
 * belongs beside the name of the thing it versions rather than among the row
 * counts, which are what the top panel is for.
 */
const isReferenceNumber = (key: string): boolean => /version$/i.test(key)

const isGroup = (value: JsonValue): value is Record<string, JsonValue> =>
  typeof value === 'object' && value !== null && !Array.isArray(value)

/** eligibilityTemplate -> "Eligibility template" */
const humanise = (key: string): string => {
  const spaced = key.replace(/([a-z0-9])([A-Z])/g, '$1 $2')
  return spaced.charAt(0).toUpperCase() + spaced.slice(1).toLowerCase()
}

function DetailRows({ entries }: { entries: [string, JsonValue][] }) {
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'minmax(140px, max-content) 1fr',
        gap: '4px 16px',
        fontSize: 12.5,
      }}
    >
      {entries.map(([key, value]) => (
        <React.Fragment key={key}>
          {/* A native title attribute never appears while the text is
              selected, and takes a second to show. A tooltip component is the
              reliable way to explain a key. */}
          <Tooltip
            title={
              KEY_HINTS[key] ? describeValues(KEY_HINTS[key]) : KEY_NOTES[key]
            }
            placement="topLeft"
          >
            <span
              style={{
                color: '#6b7280',
                cursor: hasHint(key) ? 'help' : undefined,
                borderBottom: hasHint(key) ? '1px dotted #d1d5db' : undefined,
                alignSelf: 'start',
                justifySelf: 'start',
              }}
            >
              {key}
            </span>
          </Tooltip>
          {/* A list of duplicate groups is a table, not a value. */}
          {parseDuplicateGroups(value) ? (
            <DuplicateGroups groups={parseDuplicateGroups(value) ?? []} />
          ) : /* JsonLogic is unreadable on one line, so indent it. */
          key === 'rule' ? (
            <pre
              style={{
                margin: 0,
                fontFamily: 'monospace',
                fontSize: 12,
                lineHeight: '17px',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
              }}
            >
              {formatRule(value)}
            </pre>
          ) : (
            <span style={{ fontFamily: 'monospace', wordBreak: 'break-word' }}>
              {formatValue(value)}
            </span>
          )}
        </React.Fragment>
      ))}
    </div>
  )
}

const PANEL: React.CSSProperties = {
  marginTop: 8,
  background: '#f9fafb',
  border: '1px solid #e5e7eb',
  borderRadius: 6,
  padding: '10px 12px',
}

export function StepDetail({ detail }: { detail: Record<string, JsonValue> }) {
  const entries = Object.entries(detail)
  if (entries.length === 0) return null
  // A nested object is a group the backend deliberately kept together, so give
  // it its own panel rather than flattening it into one long JSON line.
  const groups: [string, Record<string, JsonValue>][] = []
  const counts: [string, JsonValue][] = []
  const rest: [string, JsonValue][] = []
  entries.forEach(([key, value]) => {
    if (isGroup(value)) {
      groups.push([key, value])
      return
    }
    // Counts answer "what happened"; everything else is a reference an admin
    // only reads when chasing something. Mixing them buries the numbers among
    // long S3 keys.
    if (typeof value === 'number' && !isReferenceNumber(key)) {
      counts.push([key, value])
      return
    }
    rest.push([key, value])
  })
  return (
    <>
      {counts.length > 0 && (
        <div style={PANEL}>
          <DetailRows entries={sortByOutcome(counts)} />
        </div>
      )}
      {rest.length > 0 && (
        <div style={PANEL}>
          <DetailRows entries={sortByOutcome(rest)} />
        </div>
      )}
      {groups.map(([key, value]) => (
        <div key={key} style={PANEL}>
          <div
            style={{
              fontSize: 11,
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: '0.04em',
              color: '#9ca3af',
              marginBottom: 8,
            }}
          >
            {humanise(key)}
          </div>
          <DetailRows entries={Object.entries(value)} />
        </div>
      ))}
    </>
  )
}

function ProcessingDetails({
  steps,
  isLoading,
  isError,
  clientPublicKey,
  clientCompanyName,
}: {
  steps: CensusRunStepDto[]
  isLoading: boolean
  isError: boolean
  clientPublicKey?: string
  clientCompanyName?: string
}) {
  if (isLoading) {
    return (
      <Section title="Processing details" startCollapsed>
        <div style={{ padding: '24px 0', textAlign: 'center' }}>
          <Spin />
        </div>
      </Section>
    )
  }

  if (isError) {
    return (
      <Section title="Processing details" startCollapsed>
        <div style={{ padding: '16px 0', color: '#7a1f22' }}>
          Could not load processing details for this run.
        </div>
      </Section>
    )
  }

  const byStepKey = new Map(steps.map((step) => [step.stepKey, step]))

  return (
    <Section title="Processing details" startCollapsed>
      <div style={{ paddingTop: 8 }} />
      {API_STEPS.map(({ seq, stepKey, name }) => {
        const step = byStepKey.get(stepKey)
        const tone = TONE[step?.status ?? 'PENDING']
        const isReached = Boolean(step)
        return (
          <div key={stepKey} style={{ display: 'flex', gap: 14 }}>
            <StageRail
              status={step?.status}
              isLast={seq === API_STEPS.length}
            />
            <div
              style={{
                flex: 1,
                display: 'flex',
                justifyContent: 'space-between',
                gap: 16,
                paddingBottom: 18,
              }}
            >
              <div style={{ flex: 1 }}>
                <div
                  style={{
                    fontSize: 14.5,
                    fontWeight: 600,
                    color: isReached ? undefined : '#9ca3af',
                  }}
                >
                  <span style={{ color: '#9ca3af', marginRight: 6 }}>
                    {seq}.
                  </span>
                  {name}
                </div>
                <div style={{ color: tone.fg, fontSize: 12.5 }}>
                  {withCompanyName(
                    step?.result,
                    clientPublicKey,
                    clientCompanyName
                  ) ?? 'Not reached'}
                  {step?.errorCode && (
                    <span style={{ marginLeft: 8, fontFamily: 'monospace' }}>
                      ({step.errorCode})
                    </span>
                  )}
                </div>
                {step?.detail && <StepDetail detail={step.detail} />}
              </div>
              <div
                style={{
                  color: '#9ca3af',
                  fontSize: 12.5,
                  whiteSpace: 'nowrap',
                }}
              >
                {formatDuration(step?.durationMs)}
              </div>
            </div>
          </div>
        )
      })}
    </Section>
  )
}

export default ProcessingDetails
